import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "../lib/apiClient";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Loader2,
  Sparkles,
  Bot,
  Plus,
  Trash2,
  Pencil,
  Check,
  X,
  MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import MentorMessage from "./mentor/MentorMessage";
import MentorComposer from "./mentor/MentorComposer";

const formatTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

export default function MentorDrawer({
  open,
  onOpenChange,
  pendingPrompt = null,
  onPromptConsumed,
  // Feb 25 2027 · Section-scoped anchor. When set, after threads load
  // we switch to the thread whose first user message references this
  // section name (from "Explain this section") and scroll the drawer
  // to the mentor's reply for that section.
  focusSectionName = null,
  onFocusConsumed,
}) {
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeThread, setActiveThread] = useState(null); // { thread_id, messages }
  const [starters, setStarters] = useState([]);
  const [input, setInput] = useState("");
  const [loadingList, setLoadingList] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [sending, setSending] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const scrollRef = useRef(null);

  const loadThreads = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/mentor/threads");
      setThreads(data.threads || []);
      return data.threads || [];
    } catch (e) {
      toast.error("Failed to load mentor history");
      return [];
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadThread = useCallback(async (id) => {
    if (!id) return;
    setLoadingThread(true);
    try {
      const { data } = await api.get(`/mentor/threads/${id}`);
      setActiveThread(data.thread);
    } catch {
      toast.error("Failed to load conversation");
      setActiveThread(null);
    } finally {
      setLoadingThread(false);
    }
  }, []);

  // Load on open
  useEffect(() => {
    if (!open) return;
    (async () => {
      const list = await loadThreads();
      try {
        const { data } = await api.get("/mentor/starters");
        setStarters(data.starters || []);
      } catch {
        /* non-fatal */
      }
      // Feb 25 2027 · If the drawer was opened with a section anchor
      // AND there is no pendingPrompt (i.e. the founder is *re-opening*
      // to read a previous explanation), try to switch to the thread
      // whose title/preview references that section. Fallback to the
      // most-recent thread if we can't find one.
      if (list.length) {
        let target = list[0];
        if (focusSectionName && !pendingPrompt) {
          const needle = `"${focusSectionName}"`;
          const match = list.find(
            (t) =>
              (t.title || "").includes(needle) ||
              (t.last_preview || "").includes(needle),
          );
          if (match) target = match;
        }
        if (!activeId) setActiveId(target.thread_id);
        else if (
          focusSectionName &&
          !pendingPrompt &&
          target.thread_id !== activeId
        ) {
          setActiveId(target.thread_id);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Load active thread when activeId changes
  useEffect(() => {
    if (open && activeId) loadThread(activeId);
    if (!activeId) setActiveThread(null);
  }, [activeId, open, loadThread]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [activeThread?.messages?.length, sending]);

  // Feb 25 2027 · Section anchor scroll. Once the active thread is
  // loaded and its messages are painted, find the mentor bubble that
  // answered the "explain this section" prompt for `focusSectionName`
  // and scroll it into view with a brief highlight. Then consume the
  // anchor so it doesn't refire on the next re-render.
  useEffect(() => {
    if (!open || !focusSectionName) return;
    if (!activeThread || loadingThread || sending) return;
    const el = scrollRef.current?.querySelector(
      `[data-focus-section="${CSS.escape(focusSectionName)}"]`,
    );
    if (!el) return;
    // Give the layout a tick so scrollIntoView lands on the real
    // painted position rather than the pre-layout one.
    const t = window.setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("mentor-focus-flash");
      window.setTimeout(
        () => el.classList.remove("mentor-focus-flash"),
        2200,
      );
      onFocusConsumed?.();
    }, 60);
    return () => window.clearTimeout(t);
  }, [
    open,
    focusSectionName,
    activeThread,
    loadingThread,
    sending,
    onFocusConsumed,
  ]);

  const startNewThread = async (firstMessage = null) => {
    setSending(!!firstMessage);
    try {
      const { data } = await api.post("/mentor/threads", {
        first_message: firstMessage || null,
      });
      const t = data.thread;
      // Optimistically add to list head
      setThreads((prev) => [
        {
          thread_id: t.thread_id,
          title: t.title,
          message_count: (t.messages || []).length,
          last_preview: (t.messages?.slice(-1)[0]?.text || "").slice(0, 120),
          last_role: t.messages?.slice(-1)[0]?.role,
          created_at: t.created_at,
          updated_at: t.updated_at,
        },
        ...prev.filter((p) => p.thread_id !== t.thread_id),
      ]);
      setActiveId(t.thread_id);
      setActiveThread(t);
      setInput("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start chat");
    } finally {
      setSending(false);
    }
  };

  // "Explain this section" hand-off — when the drawer is opened with a seeded
  // prompt, kick off a fresh thread with it once, then clear so it doesn't refire.
  useEffect(() => {
    if (open && pendingPrompt && !sending) {
      startNewThread(pendingPrompt);
      onPromptConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pendingPrompt]);

  const sendMessage = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;

    if (!activeId) {
      // Create thread with first message
      await startNewThread(text);
      return;
    }

    setSending(true);
    // Optimistic append
    setActiveThread((t) =>
      t
        ? {
            ...t,
            messages: [
              ...(t.messages || []),
              { role: "user", text, created_at: new Date().toISOString() },
            ],
          }
        : t
    );
    setInput("");
    try {
      const { data } = await api.post(`/mentor/threads/${activeId}/message`, {
        text,
      });
      setActiveThread(data.thread);
      // Refresh thread list ordering / title
      loadThreads();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Mentor failed");
      // Rollback
      setActiveThread((t) =>
        t
          ? {
              ...t,
              messages: (t.messages || []).slice(0, -1),
            }
          : t
      );
    } finally {
      setSending(false);
    }
  };

  const deleteThread = async (id) => {
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await api.delete(`/mentor/threads/${id}`);
      setThreads((prev) => prev.filter((t) => t.thread_id !== id));
      if (activeId === id) {
        setActiveId(null);
        setActiveThread(null);
      }
    } catch {
      toast.error("Could not delete");
    }
  };

  const startRename = (t) => {
    setRenamingId(t.thread_id);
    setRenameValue(t.title || "");
  };
  const cancelRename = () => {
    setRenamingId(null);
    setRenameValue("");
  };
  const submitRename = async (id) => {
    const title = renameValue.trim().slice(0, 80);
    if (!title) return cancelRename();
    try {
      await api.patch(`/mentor/threads/${id}`, { title });
      setThreads((prev) =>
        prev.map((t) => (t.thread_id === id ? { ...t, title } : t))
      );
      if (activeThread?.thread_id === id) {
        setActiveThread((t) => (t ? { ...t, title } : t));
      }
    } catch {
      toast.error("Rename failed");
    } finally {
      cancelRename();
    }
  };

  const messages = activeThread?.messages || [];

  // Feb 25 2027 · Section-anchor target lookup. Find the mentor reply
  // that answered the "explain this section" prompt for the current
  // focus section. Prefer the assistant bubble right after the user's
  // explain prompt; fall back to the user prompt itself if the reply
  // hasn't landed yet.
  const focusTargetIndex = (() => {
    if (!focusSectionName) return -1;
    const needle = `"${focusSectionName}"`;
    const userIdx = messages.findIndex(
      (m) => m?.role === "user" && (m?.text || "").includes(needle),
    );
    if (userIdx < 0) return -1;
    const next = messages[userIdx + 1];
    return next && next.role !== "user" ? userIdx + 1 : userIdx;
  })();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-3xl p-0 flex flex-col rounded-none border-l-2 border-neutral-900 h-[100dvh] max-h-[100dvh] overflow-hidden"
        data-testid="mentor-drawer"
      >
        <SheetHeader className="p-6 pb-4 border-b-2 border-neutral-900 bg-neutral-50 shrink-0">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-[#7C5FB0] text-white flex-shrink-0">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                Founder mentor
              </div>
              <SheetTitle
                className="font-display font-black text-2xl tracking-tighter truncate"
                data-testid="mentor-title"
              >
                {activeThread?.title || "Ask your Founder Mentor"}
              </SheetTitle>
              <SheetDescription className="text-xs text-neutral-600 mt-1 leading-snug">
                Knows every business you've created. Speaks in your voice.
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 flex min-h-0">
          {/* Threads pane */}
          <aside
            className="w-56 border-r border-neutral-200 flex flex-col bg-white"
            data-testid="mentor-threads-pane"
          >
            <Button
              onClick={() => {
                setActiveId(null);
                setActiveThread(null);
                setInput("");
              }}
              data-testid="mentor-new-thread-btn"
              className="rounded-none m-3 bg-neutral-900 hover:bg-[#4A4458] text-white font-bold tracking-wide h-10"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              New chat
            </Button>
            <div className="flex-1 overflow-y-auto px-2 pb-3">
              {loadingList && threads.length === 0 ? (
                <div className="text-xs text-neutral-400 px-2 py-4">
                  Loading…
                </div>
              ) : threads.length === 0 ? (
                <div className="text-xs text-neutral-400 px-2 py-4 leading-relaxed">
                  No conversations yet. Start one →
                </div>
              ) : (
                <ul className="space-y-1">
                  {threads.map((t) => (
                    <li key={t.thread_id}>
                      <div
                        className={`group rounded-none px-2 py-2 border ${
                          activeId === t.thread_id
                            ? "bg-neutral-900 text-white border-neutral-900"
                            : "border-transparent hover:border-neutral-300 hover:bg-neutral-50 text-neutral-800"
                        }`}
                        data-testid={`mentor-thread-${t.thread_id}`}
                      >
                        {renamingId === t.thread_id ? (
                          <form
                            onSubmit={(e) => {
                              e.preventDefault();
                              submitRename(t.thread_id);
                            }}
                            className="flex items-center gap-1"
                          >
                            <Input
                              autoFocus
                              value={renameValue}
                              onChange={(e) =>
                                setRenameValue(e.target.value)
                              }
                              maxLength={80}
                              data-testid="mentor-rename-input"
                              className="h-7 text-xs rounded-none bg-white text-neutral-900"
                            />
                            <button
                              type="submit"
                              data-testid="mentor-rename-save"
                              className="p-1 hover:bg-emerald-100 hover:text-emerald-700"
                            >
                              <Check className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={cancelRename}
                              className="p-1 hover:bg-red-100 hover:text-red-700"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </form>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => setActiveId(t.thread_id)}
                              className="w-full text-left"
                              data-testid={`mentor-thread-select-${t.thread_id}`}
                            >
                              <div className="text-xs font-bold truncate">
                                {t.title || "Untitled"}
                              </div>
                              <div
                                className={`text-[10px] truncate mt-0.5 ${
                                  activeId === t.thread_id
                                    ? "text-neutral-300"
                                    : "text-neutral-500"
                                }`}
                              >
                                {t.last_preview || "—"}
                              </div>
                              <div
                                className={`text-[9px] uppercase tracking-[0.2em] mt-1 ${
                                  activeId === t.thread_id
                                    ? "text-neutral-400"
                                    : "text-neutral-400"
                                }`}
                              >
                                {formatTime(t.updated_at)} ·{" "}
                                {t.message_count || 0} msg
                              </div>
                            </button>
                            <div className="flex gap-1 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startRename(t);
                                }}
                                data-testid={`mentor-thread-rename-${t.thread_id}`}
                                className={`text-[9px] uppercase tracking-[0.2em] flex items-center gap-1 px-1.5 py-0.5 ${
                                  activeId === t.thread_id
                                    ? "hover:bg-neutral-700"
                                    : "hover:bg-neutral-200"
                                }`}
                              >
                                <Pencil className="w-3 h-3" />
                                Rename
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteThread(t.thread_id);
                                }}
                                data-testid={`mentor-thread-delete-${t.thread_id}`}
                                className={`text-[9px] uppercase tracking-[0.2em] flex items-center gap-1 px-1.5 py-0.5 ${
                                  activeId === t.thread_id
                                    ? "hover:bg-red-700"
                                    : "hover:bg-red-100 hover:text-red-700"
                                }`}
                              >
                                <Trash2 className="w-3 h-3" />
                                Delete
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>

          {/* Conversation pane */}
          <div className="flex-1 flex flex-col min-h-0">
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto p-6 space-y-4 min-h-0"
              data-testid="mentor-messages"
            >
              {loadingThread ? (
                <div className="flex items-center justify-center text-neutral-500 text-sm py-12">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading conversation…
                </div>
              ) : !activeThread ? (
                <div className="max-w-md mx-auto pt-8">
                  <div className="text-center">
                    <div className="w-12 h-12 mx-auto bg-[#7C5FB0] text-white flex items-center justify-center mb-3">
                      <Sparkles className="w-6 h-6" />
                    </div>
                    <div className="font-display font-black text-2xl tracking-tighter mb-1">
                      Start a new chat
                    </div>
                    <p className="text-sm text-neutral-600 leading-relaxed mb-6">
                      Your mentor has read every plan, goal, compliance item,
                      and weekly focus across all your businesses.
                    </p>
                  </div>
                  {starters.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-bold mb-2">
                        Try asking
                      </div>
                      {starters.map((s, i) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => startNewThread(s)}
                          disabled={sending}
                          data-testid={`mentor-starter-${i}`}
                          className="w-full text-left text-sm leading-snug px-4 py-3 border border-neutral-200 hover:border-neutral-900 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-start gap-2"
                        >
                          <MessageSquare className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#7C5FB0]" />
                          <span>{s}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {messages.length === 0 && (
                    <div className="text-center text-sm text-neutral-500 mt-12 max-w-sm mx-auto leading-relaxed">
                      Send a message to start.
                    </div>
                  )}
                  {messages.map((m, i) => (
                    // Messages stream in via SSE/POST in append-only
                    // order — they never reorder once landed — and there
                    // is no stable id for the optimistic local "sending"
                    // bubble. So a synthetic `idx-content-hash` key is
                    // the honest stable identity here.
                    <div
                      key={`msg-${i}-${(m.text || m.content || "").slice(0, 16)}`}
                      data-focus-section={
                        i === focusTargetIndex ? focusSectionName : undefined
                      }
                      className={
                        i === focusTargetIndex
                          ? "mentor-focus-anchor scroll-mt-4"
                          : undefined
                      }
                    >
                      <MentorMessage msg={m} />
                    </div>
                  ))}
                  {sending && (
                    <div
                      className="flex items-start gap-2 text-sm text-neutral-500"
                      data-testid="mentor-typing"
                    >
                      <div className="w-7 h-7 flex items-center justify-center bg-[#7C5FB0] text-white flex-shrink-0">
                        <Bot className="w-4 h-4" />
                      </div>
                      <div className="flex items-center gap-1.5 px-3 py-2">
                        <span className="w-1.5 h-1.5 bg-neutral-500 rounded-full animate-pulse" />
                        <span className="w-1.5 h-1.5 bg-neutral-500 rounded-full animate-pulse [animation-delay:150ms]" />
                        <span className="w-1.5 h-1.5 bg-neutral-500 rounded-full animate-pulse [animation-delay:300ms]" />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <MentorComposer
              input={input}
              onChange={setInput}
              onSubmit={sendMessage}
              sending={sending}
              activeId={activeId}
            />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
